import Section from '@components/common/Section/Section'

import useOnScreen from '@lib/hooks/useOnScreen'
import cn from 'clsx'
import React, { FC, useEffect, useRef } from 'react'

import { MediaWithCropsFragment } from '@lib/umbraco/generated/types.generated'
import { Swiper, SwiperSlide } from 'swiper/react'
import { UmbracoLink } from 'types/umbracoLink'
import HeroImage from '../HeroImage/HeroImage'
import styles from './HeroMediaSlider.module.scss'

export interface ImageContentElement {
  title?: string
  subtitle?: string
  darkBackground?: boolean
  mediaPicker?: MediaWithCropsFragment
  ctaLink?: UmbracoLink
}

type FullScreenSliderProps = {
  images?: ImageContentElement[]
  timePerFrame?: number
  fadeIn?: boolean
  autoRun?: boolean
  viewHeight?: number
  screenWidth?: number
  screenHeight?: number
}

const HeroMediaSlider: FC<FullScreenSliderProps> = ({
  images,
  timePerFrame,
  autoRun,
  viewHeight,
  fadeIn,
}) => {
  const [swiper, setSwiper] = React.useState<any>()
  const progress = useRef(0)
  const container = useRef<HTMLDivElement>(null)
  let containerOnScreen = useOnScreen(container)

  useEffect(() => {
    if (autoRun) {
      const calibratedTPFInMs = (timePerFrame! * 1000) / 10
      const amountToUpdatePerEvent = 100 / calibratedTPFInMs

      const interval = setInterval(() => {
        const el = document.getElementById(
          `heroMediaImage_${swiper!.realIndex}`,
        )
        if (!el) return
        el.style.width = `${progress.current}%`
        progress.current = progress.current + amountToUpdatePerEvent
        if (progress.current >= 100) {
          swiper!.slideNext()
        }
      }, 10)

      return () => clearInterval(interval)
    }
  }, [swiper, autoRun])

  useEffect(() => {
    const keydownHandler = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowRight':
          swiper!.slideNext()
          break

        case 'ArrowLeft':
          swiper!.slidePrev()
          break
      }
    }

    if (window && containerOnScreen) {
      window.addEventListener('keydown', keydownHandler)

      return () => {
        window.removeEventListener('keydown', keydownHandler)
      }
    }
  }, [containerOnScreen, swiper])

  const handleRealIndexChange = (realIndex: number) => {
    progress.current = 0
    const AllEl = document.querySelectorAll(`.${styles.foreground}`)
    AllEl.forEach((el: any, index: number) => {
      el.style.width = null
      if (realIndex > index) {
        el.classList.add(styles.fullyLoaded)
      } else {
        el.classList.remove(styles.fullyLoaded)
      }
    })
  }

  return (
    <Section
      disableFadeUp
      className={cn(styles.container, fadeIn && styles.useFadeIn)}
      style={{ height: viewHeight ? `${viewHeight}vh` : `100vh` }}
      ref={container}
    >
      <div className={styles.swiperContainer}>
        <Swiper
          onInit={(swiper) => {
            setSwiper(swiper)
          }}
          loop
          slidesPerView={1}
          onRealIndexChange={(e) => {
            handleRealIndexChange(e.realIndex)
          }}
          className={styles.swiper}
        >
          {images &&
            images.map((image, index) => (
              <SwiperSlide key={index}>
                <HeroImage
                  image={image.mediaPicker}
                  richText={image.title}
                  darkenBackground={image.darkBackground}
                  ctaLink={image.ctaLink!}
                />
              </SwiperSlide>
            ))}
        </Swiper>
      </div>
      {swiper && (
        <div className={`${styles.timelineContainer} ${styles.foldMoreActive}`}>
          <ul className={styles.timeline}>
            {images &&
              images.map((_, index) => (
                <li key={index} className={styles.timelineItem}>
                  <div className={styles.background} />
                  <div
                    className={styles.foreground}
                    id={`heroMediaImage_${index}`}
                  />
                </li>
              ))}
          </ul>
        </div>
      )}
    </Section>
  )
}

export default HeroMediaSlider
